import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Check } from "lucide-react";

/**
 * Cinematic Postun Hazır page transition.
 *
 * Sequence:
 *  1. Blue panel sweeps in from the right (clip-path-like: scaleX 0→1)
 *  2. Centered Postun Hazır logo zooms up + yellow check tick pulses
 *  3. Panel sweeps out to the left → revealing the new page underneath
 *
 * Triggered by Header link onClick which sets `active=true`. After ~1.0s the
 * caller programmatically navigates and the overlay exits (~0.6s sweep-out).
 */
export default function PostunHazirTransition({ active }) {
  return (
    <AnimatePresence>
      {active && (
        <motion.div
          key="postun-overlay"
          className="fixed inset-0 z-[120] pointer-events-none"
          data-testid="postun-transition-overlay"
          initial={{ opacity: 1 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 1 }}
        >
          {/* Sweep panel — deep blue */}
          <motion.div
            className="absolute inset-0"
            style={{
              background:
                "linear-gradient(120deg, #1E40AF 0%, #2563EB 55%, #3B82F6 100%)",
              transformOrigin: "right center",
            }}
            initial={{ scaleX: 0 }}
            animate={{ scaleX: 1 }}
            exit={{ scaleX: 0, transformOrigin: "left center" }}
            transition={{
              duration: 0.55,
              ease: [0.76, 0, 0.24, 1],
              exit: { duration: 0.55, ease: [0.76, 0, 0.24, 1] },
            }}
          />

          {/* Soft cyan glow + grain on top of panel */}
          <motion.div
            className="absolute inset-0 mix-blend-screen"
            style={{
              background:
                "radial-gradient(ellipse at 60% 50%, rgba(6,182,212,0.45) 0%, rgba(6,182,212,0) 60%)",
            }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4, delay: 0.25 }}
          />

          {/* Center logo (badge + wordmark) — zooms up then settles */}
          <motion.div
            className="absolute inset-0 flex items-center justify-center"
            initial={{ opacity: 0, scale: 0.6 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 1.25 }}
            transition={{
              duration: 0.55,
              delay: 0.35,
              ease: [0.22, 1, 0.36, 1],
            }}
          >
            <div className="flex flex-col items-center gap-5">
              <div className="relative">
                <motion.div
                  className="absolute inset-0 rounded-full bg-postun-cyan/40 blur-2xl"
                  animate={{ scale: [1, 1.4, 1] }}
                  transition={{ duration: 1.2, repeat: Infinity, ease: "easeInOut" }}
                />
                <div className="relative flex h-32 w-32 items-center justify-center rounded-full bg-cream shadow-[0_18px_60px_-15px_rgba(252,211,77,0.7)]">
                  <img
                    src="/brand/postun-hazir-logo.png"
                    alt="Postun Hazır"
                    className="h-24 w-24 object-contain"
                    draggable="false"
                  />
                </div>
              </div>
              <div className="flex flex-col items-center gap-1 text-cream">
                <span className="text-xs uppercase tracking-[0.4em] text-postun-yellow font-semibold">
                  Postun
                </span>
                <span
                  className="font-display font-extrabold lowercase leading-none"
                  style={{ fontSize: "clamp(2.5rem, 7vw, 5rem)", letterSpacing: "-0.04em" }}
                >
                  hazır
                </span>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
